import React from 'react';
import { BadgeCheck, CircleDot, Clock, Footprints, Info, Mail, TabletSmartphone, Undo2, UserX } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';

const ICONS = { tablet: TabletSmartphone, dot: CircleDot, footprints: Footprints, clock: Clock, userx: UserX, badge: BadgeCheck, mail: Mail, undo: Undo2, info: Info };

/** A confirm dialog that says what will happen, point by point, before it does (the prototype's `cdVals`). */
export function ConfirmPointsDialog({
    open, onClose, onConfirm, title, sub, points, primary, danger = false, busy = false, disabled = false, testId, children,
}) {
    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) onClose?.(); }}>
            <DialogContent data-testid={testId} className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    {sub && <DialogDescription>{sub}</DialogDescription>}
                </DialogHeader>
                <ul className="space-y-2">
                    {points.map((point) => {
                        const Icon = ICONS[point.icon] || Info;
                        return (
                            <li key={point.text} className="flex items-start gap-2.5 text-sm text-stone-700">
                                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-stone-500" aria-hidden="true" /><span>{point.text}</span>
                            </li>
                        );
                    })}
                </ul>
                {children}
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={onConfirm} disabled={busy || disabled} className={danger ? 'bg-red-700 text-white hover:bg-red-800' : ''} data-testid={`${testId}-confirm`}>
                        {busy ? 'Saving…' : primary}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

/** Start event: opens check-in and moves nobody (D4, 1013-31). */
export function StartEventDialog({ open, onClose, onConfirm, busy, eventLine, goingCount, startClock }) {
    return (
        <ConfirmPointsDialog
            open={open} onClose={onClose} onConfirm={onConfirm} busy={busy} testId="start-event-dialog"
            title="Start the event now?" sub={eventLine} primary="Start event"
            points={[
                { icon: 'tablet', text: 'Check-in opens. Switch guests in from the Check-in view, or use Check in on a Going card.' },
                { icon: 'dot', text: `Nobody moves. The ${plural(goingCount, 'Going guest', 'Going guests')} stay${goingCount === 1 ? 's' : ''} in Going while they can still arrive.` },
                { icon: 'footprints', text: 'Walk-ins can be created and checked in at the door.' },
                { icon: 'clock', text: `If nobody starts it, it starts on its own${startClock ? ` at ${startClock}` : ''} on the day.` },
            ]}
        />
    );
}

/** Reopen check-in: puts back exactly the guests End moved (P3). */
export function ReopenEventDialog({ open, onClose, onConfirm, busy }) {
    return (
        <ConfirmPointsDialog
            open={open} onClose={onClose} onConfirm={onConfirm} busy={busy} testId="reopen-event-dialog"
            title="Reopen check-in?" sub="The event goes back to Live" primary="Reopen check-in"
            points={[
                { icon: 'undo', text: 'The guests moved to No-show when it ended go back to Going.' },
                { icon: 'info', text: 'Everything else you did after it ended is kept.' },
            ]}
        />
    );
}
